import I18n from 'I18n';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import PropTypes from 'prop-types';
import { Component } from 'react';
import { fromMetricKey } from 'reporting-data/v2/dataset/datasetMetrics';
import { Button } from 'foundations-components/transitional/components/Button';
import { ChangeArrow } from 'foundations-components/transitional/components/ChangeArrow';
import { Well, WellItem, WellLabel, WellBigNumber, WellHelpText } from 'foundations-components/transitional/components/Well';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { HelpIcon } from 'foundations-components/transitional/components/HelpIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledWell = ({
  children,
  compact
}) => {
  if (compact) {
    return /*#__PURE__*/_jsx(Well, {
      padding: "100",
      children: children
    });
  }
  return /*#__PURE__*/_jsx(Well, {
    children: children
  });
};
const StyledWellItem = ({
  children,
  compact
}) => {
  if (compact) {
    return /*#__PURE__*/_jsx(WellItem, {
      "data-test-id": "datawell-item",
      margin: "100",
      children: children
    });
  }
  return /*#__PURE__*/_jsx(WellItem, {
    "data-test-id": "datawell-item",
    children: children
  });
};
class Datawell extends Component {
  constructor(...args) {
    super(...args);
    this.renderHead = (cell, renderHeader) => {
      const label = cell.get('tooltip') ? /*#__PURE__*/_jsx(HelpIcon, {
        title: cell.get('tooltip'),
        children: cell.get('label')
      }) : cell.get('label');
      return /*#__PURE__*/_jsx(WellLabel, {
        "data-test-id": "datawell-item-label",
        children: renderHeader ? renderHeader(cell) : label
      });
    };
    this.renderBody = (cell, renderBody, getOnPointClick) => {
      const valueLabel = cell.get('valueLabel');
      const onPointClick = getOnPointClick && getOnPointClick(cell);
      const propertyName = fromMetricKey(cell.get('field')).property;
      const datasetName = cell.get('dataset', null);
      const hasDatasetName = datasetName !== null;
      const body = renderBody ? renderBody(cell) : valueLabel;
      const optionalProps = Object.assign({}, hasDatasetName && {
        'data-test-datasetname': datasetName
      });
      return /*#__PURE__*/_jsx(WellBigNumber, {
        inlineSize: "100%",
        children: onPointClick ? /*#__PURE__*/_jsx(Button, Object.assign({
          use: "link",
          truncate: true,
          "data-test-id": `${propertyName}-drilldown-button`,
          onClick: () => onPointClick(cell)
        }, optionalProps, {
          children: body
        })) : body
      });
    };
    this.renderDelta = cell => {
      if (!cell.get('compare')) {
        return null;
      }
      const cellValue = cell.get('value');
      const compare = cell.get('compare');
      if (!(typeof compare === 'number' && isFinite(compare))) {
        return /*#__PURE__*/_jsx(WellHelpText, {
          "data-test-id": "datawell-item-help-text",
          children: /*#__PURE__*/_jsx("span", {
            children: "-"
          })
        });
      }
      if (cellValue === compare) {
        return /*#__PURE__*/_jsx(WellHelpText, {
          "data-test-id": "datawell-item-help-text",
          children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
            message: 'reporting-visualizations.deltas.unchanged'
          })
        });
      }
      const isPercent = fromMetricKey(cell.field).type === 'PERCENTILES';
      const value = isPercent ? Math.abs((compare - cellValue) * 100) : Math.abs((compare - cellValue) / compare * 100);
      const direction = cellValue - compare > 0 ? 'increase' : 'decrease';
      const label = value < 0.01 ? `< ${I18n.formatPercentage(0.01, {
        precision: 2
      })}` : I18n.formatPercentage(value, {
        precision: 2
      });
      let colorToken = direction === 'increase' ? 'fill-positive-default' : 'fill-alert-default';
      if (cell.inverseDeltas) {
        colorToken = colorToken === 'fill-alert-default' ? 'fill-positive-default' : 'fill-alert-default';
      }
      return /*#__PURE__*/_jsx(Tooltip, {
        title: I18n.text('reporting-visualizations.deltas.comparison-tooltip', {
          count: cell.get('compareValueLabel')
        }),
        placement: "bottom",
        children: /*#__PURE__*/_jsxs(WellHelpText, {
          "data-test-id": "datawell-item-help-text",
          children: [/*#__PURE__*/_jsx(ChangeArrow, {
            use: direction,
            color: colorToken
          }), label]
        })
      });
    };
    this.renderSubtitle = cell => {
      if (cell.get('compare')) {
        return this.renderDelta(cell);
      }
      const {
        subtitleLabel,
        subtitleValueLabel
      } = cell.toJS();
      if (subtitleValueLabel && subtitleLabel) {
        return /*#__PURE__*/_jsx(WellHelpText, {
          "data-test-id": "datawell-item-help-text",
          children: `${subtitleLabel}: ${subtitleValueLabel}`
        });
      }
      return null;
    };
  }
  componentDidMount() {
    var _this$props$onReportV, _this$props;
    (_this$props$onReportV = (_this$props = this.props).onReportVisualize) === null || _this$props$onReportV === void 0 || _this$props$onReportV.call(_this$props);
  }
  render() {
    var _data$first;
    const {
      data,
      events: {
        getOnPointClick,
        getHeader,
        getBody
      } = {}
    } = this.props.datawell;
    const compact = !!(data !== null && data !== void 0 && (_data$first = data.first()) !== null && _data$first !== void 0 && _data$first.getIn(['layer', 'compact']));
    return /*#__PURE__*/_jsx("div", {
      style: {
        flex: 1
      },
      children: /*#__PURE__*/_jsx(StyledWell, {
        compact: compact,
        children: data.map((cell, index) => /*#__PURE__*/_jsxs(StyledWellItem, {
          compact: compact,
          children: [this.renderHead(cell, getHeader), this.renderBody(cell, getBody, getOnPointClick), this.renderSubtitle(cell)]
        }, index))
      })
    });
  }
}
Datawell.propTypes = {
  datawell: PropTypes.object,
  onReportVisualize: PropTypes.func
};
export default Datawell;